import { io } from 'socket.io-client';

// En production, utiliser l'origine du site (même serveur)
// En développement, le socket.io tourne sur le même serveur que l'API :
// on dérive l'URL depuis REACT_APP_API_URL (en retirant le suffixe /api)
// pour rester toujours aligné avec le backend, quel que soit le port.
const getSocketUrl = () => {
  if (process.env.NODE_ENV === 'production') {
    return window.location.origin;
  }
  if (process.env.REACT_APP_SOCKET_URL) {
    return process.env.REACT_APP_SOCKET_URL;
  }
  const apiUrl = process.env.REACT_APP_API_URL;
  if (apiUrl) {
    return apiUrl.replace(/\/api\/?$/, '');
  }
  return 'http://localhost:9001';
};

const SOCKET_URL = getSocketUrl();

let socket = null;
let reconnectAttempts = 0;
const MAX_RECONNECT_ATTEMPTS = 5;

// Listeners globaux
const globalListeners = new Map();

// Initialiser la connexion socket
export const initSocket = (token) => {
  if (socket?.connected) {
    console.log('Socket déjà connecté');
    return socket;
  }

  socket = io(SOCKET_URL, {
    auth: { token },
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    reconnectionAttempts: MAX_RECONNECT_ATTEMPTS
  });

  socket.on('connect', () => {
    console.log('🔌 Socket connecté:', socket.id);
    reconnectAttempts = 0;

    // Demander le compteur de messages non lus
    socket.emit('notification:get-unread');

    // Ré-attacher les listeners globaux après reconnexion
    globalListeners.forEach((callback, event) => {
      socket.off(event);
      socket.on(event, callback);
    });
  });

  socket.on('disconnect', (reason) => {
    console.log('🔌 Socket déconnecté:', reason);
  });

  socket.on('connect_error', (error) => {
    console.error('Erreur connexion socket:', error.message);
    reconnectAttempts++;

    if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      console.log('Max tentatives atteint, arrêt reconnexion');
      socket.disconnect();
    }
  });

  socket.on('error', (error) => {
    console.error('Erreur socket:', error);
  });

  return socket;
};

// Déconnecter le socket
export const disconnectSocket = () => {
  if (socket) {
    socket.disconnect();
    socket = null;
    globalListeners.clear();
    console.log('🔌 Socket déconnecté manuellement');
  }
};

// Obtenir l'instance socket
export const getSocket = () => socket;

// Vérifier si connecté
export const isConnected = () => socket?.connected ?? false;

// ==================== ÉVÉNEMENTS MESSAGES ====================

// Rejoindre une conversation
export const joinConversation = (conversationId) => {
  if (socket?.connected) {
    socket.emit('conversation:join', conversationId);
  }
};

// Quitter une conversation
export const leaveConversation = (conversationId) => {
  if (socket?.connected) {
    socket.emit('conversation:leave', conversationId);
  }
};

// Envoyer un message via socket
export const sendMessage = (conversationId, message) => {
  return new Promise((resolve, reject) => {
    if (!socket?.connected) {
      reject(new Error('Socket non connecté'));
      return;
    }

    socket.emit('message:send', { conversationId, message });

    // Écouter la réponse (le message reviendra via message:received)
    const timeout = setTimeout(() => {
      reject(new Error('Timeout envoi message'));
    }, 5000);

    const handler = (newMessage) => {
      if (newMessage.conversation_id === conversationId) {
        clearTimeout(timeout);
        socket.off('message:received', handler);
        resolve(newMessage);
      }
    };

    socket.once('message:received', handler);
  });
};

// Indicateurs de frappe
export const startTyping = (conversationId) => {
  if (socket?.connected) {
    socket.emit('typing:start', conversationId);
  }
};

export const stopTyping = (conversationId) => {
  if (socket?.connected) {
    socket.emit('typing:stop', conversationId);
  }
};

// Marquer les messages comme lus
export const markMessagesAsRead = (conversationId) => {
  if (socket?.connected) {
    socket.emit('messages:read', { conversationId });
  }
};

// ==================== LISTENERS ====================

// Ajouter un listener pour les nouveaux messages
export const onNewMessage = (callback) => {
  if (socket) {
    socket.on('message:received', callback);
  }
  return () => socket?.off('message:received', callback);
};

// Ajouter un listener pour les notifications de nouveaux messages
export const onNewMessageNotification = (callback) => {
  if (socket) {
    socket.on('notification:new-message', callback);
  }
  globalListeners.set('notification:new-message', callback);
  return () => {
    socket?.off('notification:new-message', callback);
    globalListeners.delete('notification:new-message');
  };
};

// Ajouter un listener pour le compteur de messages non lus
export const onUnreadCountUpdate = (callback) => {
  if (socket) {
    socket.on('notification:unread-count', callback);
  }
  globalListeners.set('notification:unread-count', callback);
  return () => {
    socket?.off('notification:unread-count', callback);
    globalListeners.delete('notification:unread-count');
  };
};

// Ajouter un listener pour l'indicateur de frappe
export const onTypingUpdate = (callback) => {
  if (socket) {
    socket.on('typing:update', callback);
  }
  return () => socket?.off('typing:update', callback);
};

// Ajouter un listener pour les messages marqués comme lus
export const onMessagesRead = (callback) => {
  if (socket) {
    socket.on('messages:marked-read', callback);
  }
  return () => socket?.off('messages:marked-read', callback);
};

// Ajouter un listener pour les notifications admin
export const onAdminNotification = (callback) => {
  if (socket) {
    socket.on('admin-notification:new', callback);
  }
  globalListeners.set('admin-notification:new', callback);
  return () => {
    socket?.off('admin-notification:new', callback);
    globalListeners.delete('admin-notification:new');
  };
};

// Demander le compteur de messages non lus
export const requestUnreadCount = () => {
  if (socket?.connected) {
    socket.emit('notification:get-unread');
  }
};

export default {
  initSocket,
  disconnectSocket,
  getSocket,
  isConnected,
  joinConversation,
  leaveConversation,
  sendMessage,
  startTyping,
  stopTyping,
  markMessagesAsRead,
  onNewMessage,
  onNewMessageNotification,
  onUnreadCountUpdate,
  onTypingUpdate,
  onMessagesRead,
  onAdminNotification,
  requestUnreadCount
};
